/**
 * Marketer Selector Component
 *
 * Component for selecting a marketer from the list following clean architecture patterns
 */

import React, {useMemo} from 'react';
import {Marketer} from '../models/report.model';
import AppSelect from '../../../Presentation/components/ui/AppSelect';
import {AppCardTitle, AppText, AppBodyText, AppSmallText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import {InlineLoadingState} from '../../../Presentation/components/loading/InlineLoadingState';

interface MarketerSelectorProps {
  marketers: Marketer[];
  selectedMarketer: Marketer | null;
  onMarketerSelect: (marketerId: number) => void;
  loading?: boolean;
}

export const MarketerSelector: React.FC<MarketerSelectorProps> = ({
  marketers,
  selectedMarketer,
  onMarketerSelect,
  loading = false
}) => {
  // Memoize options to prevent unnecessary re-renders
  const marketerOptions = useMemo(() =>
    marketers.map(marketer => ({
      value: marketer.id,
      label: marketer.territory ? `${marketer.name} - ${marketer.territory}` : marketer.name
    })), [marketers]
  );

  // Handle marketer selection
  const handleMarketerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value) {
      onMarketerSelect(Number(value));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <AppCardTitle className="text-gray-900 mb-2">
          اختيار المسوق
        </AppCardTitle>
        <AppText className="text-gray-600">
          اختر مسوق لعرض تقاريره المالية والمبيعات
        </AppText>
      </div>

      {/* Marketer Selection */}
      <div>
        <AppSelect
          label="المسوق"
          value={selectedMarketer?.id?.toString() || ''}
          onChange={handleMarketerChange}
          options={marketerOptions}
          disabled={loading}
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-4">
          <InlineLoadingState message="جاري تحميل المسوقين..." />
        </div>
      )}

      {/* Selected Marketer Info */}
      {selectedMarketer && !loading && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-brand-200 p-6">
          <div className="space-y-4">
            {/* Marketer Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <AppCardTitle className="text-brand-900 mb-1">
                  {selectedMarketer.name}
                </AppCardTitle>
                <AppBodyText className="text-brand-700">
                  المنطقة: {selectedMarketer.territory || 'غير محدد'}
                </AppBodyText>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedMarketer.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedMarketer.status === 'active' ? 'نشط' : 'غير نشط'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl">👤</div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-brand-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900 font-zain">
                  {selectedMarketer.total_stores}
                </div>
                <AppSmallText className="text-brand-700">
                  إجمالي المتاجر
                </AppSmallText>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900 font-zain">
                  {selectedMarketer.total_sales.toLocaleString()}
                </div>
                <AppSmallText className="text-brand-700">
                  إجمالي المبيعات (ريال)
                </AppSmallText>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!loading && marketers.length === 0 && (
        <Card className="p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <AppCardTitle className="text-gray-500 mb-2">
            لا توجد مسوقين متاحين
          </AppCardTitle>
          <AppText className="text-gray-400">
            لا يوجد مسوقين مسجلين في النظام حالياً
          </AppText>
        </Card>
      )}
    </div>
  );
};
